import React, { useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar, ChevronDown } from 'lucide-react';

// Convierte un string 'YYYY-MM-DD' (lo que entrega <input type="date">) en un
// ISO string usando **medianoche LOCAL**, igual que `toISODate` en ReportsPage.
// Esto garantiza que las fechas elegidas por el usuario y las que vienen por
// defecto (o por filtros rápidos) usen la misma referencia temporal.
const dateInputToLocalISO = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toISOString();
};

const ReportsFilters = ({
  isCentroLavado,
  laundries,
  idLavanderia,
  setIdLavanderia,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  loading,
  canQuery,
  onApply,
}) => {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const quickFiltersRef = useRef(null);
  const buttonRef = useRef(null);
  const [showQuickFilters, setShowQuickFilters] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0 });

  useEffect(() => {
    if (showQuickFilters && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 400;
      const spaceBelow = window.innerHeight - rect.bottom;
      
      if (spaceBelow < dropdownHeight) {
        setDropdownPosition({
          top: rect.top - dropdownHeight + window.scrollY,
          left: rect.left + window.scrollX,
          openUpward: true
        });
      } else {
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          openUpward: false
        });
      }
    }
  }, [showQuickFilters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quickFiltersRef.current && !quickFiltersRef.current.contains(event.target)) {
        setShowQuickFilters(false);
      }
    };

    if (showQuickFilters) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showQuickFilters]);

  const applyQuickFilter = (filterType) => {
    const now = new Date();
    let start, end;

    switch (filterType) {
      case 'thisWeek':
        const dayOfWeek = now.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff + 6, 23, 59, 59);
        break;
      
      case 'lastWeek':
        const lastWeekDay = now.getDay();
        const lastWeekDiff = lastWeekDay === 0 ? 6 : lastWeekDay - 1;
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - lastWeekDiff - 7);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - lastWeekDiff - 1, 23, 59, 59);
        break;
      
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      
      default:
        return;
    }

    setStartDate(start.toISOString());
    setEndDate(end.toISOString());
    setShowQuickFilters(false);
    
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <style>{`
        input.date-input::-webkit-calendar-picker-indicator { display: none; }
        input.date-input { -webkit-appearance: none; appearance: none; background-image: none; }
      `}</style>

      {!isCentroLavado && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">Centro de Lavado</Label>
          <select
            className="h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-white shadow-sm hover:border-indigo-300 dark:hover:border-indigo-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors outline-none"
            value={idLavanderia}
            onChange={(e) => setIdLavanderia(e.target.value)}
          >
            <option value="">Seleccione...</option>
            {laundries.map(l => (
              <option key={l._id} value={l._id}>{l.nombre}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex flex-col gap-1.5 relative">
        <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">Filtros Rápidos</Label>
        <div className="relative" ref={quickFiltersRef}>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setShowQuickFilters(!showQuickFilters)}
            className="h-10 px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 shadow-sm hover:border-indigo-300 dark:hover:border-indigo-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors outline-none flex items-center gap-2 min-w-[140px]"
          >
            <Calendar className="w-4 h-4" />
            <span>Períodos</span>
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showQuickFilters ? 'rotate-180' : ''}`} />
          </button>
          
          {showQuickFilters && (
            <div 
              className="fixed w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[9999] py-1"
              style={{
                top: `${dropdownPosition.top + 2}px`,
                left: `${dropdownPosition.left}px`,
              }}
            >
              <button
                type="button"
                onClick={() => applyQuickFilter('thisWeek')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
              >
                Esta Semana
              </button>
              <button
                type="button"
                onClick={() => applyQuickFilter('lastWeek')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
              >
                Semana Anterior
              </button>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
              <button
                type="button"
                onClick={() => applyQuickFilter('thisMonth')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors font-medium"
              >
                Este Mes
              </button>
              <button
                type="button"
                onClick={() => applyQuickFilter('lastMonth')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors font-medium"
              >
                Mes Anterior
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5 relative">
        <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">Desde</Label>
        <Input
          ref={startDateRef}
          type="date"
          className="pr-10 date-input rounded-lg border-gray-200 dark:border-gray-700 shadow-sm hover:border-indigo-300 dark:hover:border-indigo-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
          style={{ colorScheme: 'light' }}
          value={startDate.slice(0,10)}
          onChange={(e) => {
            const iso = dateInputToLocalISO(e.target.value);
            if (iso) setStartDate(iso);
          }}
        />
        <button
          type="button"
          aria-label="Abrir calendario desde"
          className="absolute right-2 bottom-2.5 text-gray-400 hover:text-indigo-500 transition-colors"
          onClick={() => startDateRef.current?.showPicker?.()}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col gap-1.5 relative">
        <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">Hasta</Label>
        <Input
          ref={endDateRef}
          type="date"
          className="pr-10 date-input rounded-lg border-gray-200 dark:border-gray-700 shadow-sm hover:border-indigo-300 dark:hover:border-indigo-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
          style={{ colorScheme: 'light' }}
          value={endDate.slice(0,10)}
          onChange={(e) => {
            const iso = dateInputToLocalISO(e.target.value);
            if (iso) setEndDate(iso);
          }}
        />
        <button
          type="button"
          aria-label="Abrir calendario hasta"
          className="absolute right-2 bottom-2.5 text-gray-400 hover:text-indigo-500 transition-colors"
          onClick={() => endDateRef.current?.showPicker?.()}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>
      </div>

      <Button
        onClick={onApply}
        disabled={loading || !canQuery}
        className="h-10 px-5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md shadow-indigo-200 dark:shadow-indigo-900/30 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:shadow-none"
      >
        {loading ? 'Cargando...' : 'Consultar'}
      </Button>
    </div>
  );
};

export default ReportsFilters;
